/**
 * Server-side CRS transforms (/api/wkt/transform) + async fallbacks when proj4js cannot handle a CRS.
 */
import axios from 'axios';
import { API_BASE_URL, ENVIRONMENT } from '../../../API/api';
import { getWktToProj4Converter, latLngToProjectedXY, projectedBboxToLatLngBounds } from './wktCrsToProj4';
import { normalizeWktInput } from './wktNormalize';

function buildTransformUrl() {
  const isProductionDomain =
    typeof window !== 'undefined' && window.location.hostname.includes('123bimlive.com');
  const baseUrl = ENVIRONMENT === 'production' || isProductionDomain ? '' : API_BASE_URL;
  return baseUrl ? `${API_BASE_URL}/api/wkt/transform` : '/api/wkt/transform';
}

const POINT_CHUNK = 400;

/**
 * @param {Array<{ lat: number, lng: number }>} points
 * @returns {{ south: number, north: number, west: number, east: number } | null}
 */
export function latLngPointsToBounds(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  const lats = [];
  const lngs = [];
  for (const p of points) {
    const lat = Number(p?.lat);
    const lng = Number(p?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    lats.push(lat);
    lngs.push(lng);
  }
  return {
    south: Math.min(...lats),
    north: Math.max(...lats),
    west: Math.min(...lngs),
    east: Math.max(...lngs),
  };
}

/**
 * @param {string} wkt
 * @param {number[][]} points
 * @param {'to_wgs84'|'from_wgs84'} mode
 * @returns {Promise<number[][]|null>}
 */
export async function wktTransformPoints(wkt, points, mode) {
  const wktNorm = normalizeWktInput(wkt);
  if (!wktNorm || !points?.length) return null;
  const url = buildTransformUrl();
  if (points.length <= POINT_CHUNK) {
    const res = await axios.post(url, { wkt: wktNorm, points, mode }, { timeout: 120000 });
    return res.data?.points ?? null;
  }
  const all = [];
  for (let i = 0; i < points.length; i += POINT_CHUNK) {
    const chunk = points.slice(i, i + POINT_CHUNK);
    const res = await axios.post(url, { wkt: wktNorm, points: chunk, mode }, { timeout: 120000 });
    const part = res.data?.points;
    if (!Array.isArray(part) || part.length !== chunk.length) return null;
    all.push(...part);
  }
  return all;
}

/**
 * @param {{ min_x: number, max_x: number, min_y: number, max_y: number }} bbox
 * @param {string} wkt
 * @param {string|null|undefined} proj4String from /api/wkt/validate crs.proj4
 */
export async function projectedBboxToLatLngBoundsAsync(bbox, wkt, proj4String) {
  const quad = await projectedBboxToLatLngQuadAsync(bbox, wkt, proj4String);
  return latLngPointsToBounds(quad);
}

/**
 * @param {Array<[number, number]>} projectedPoints
 * @param {string} wkt
 * @param {string|null|undefined} proj4String from /api/wkt/validate crs.proj4
 * @returns {Promise<Array<{ lat: number, lng: number }> | null>}
 */
export async function projectedPointsToLatLngAsync(projectedPoints, wkt, proj4String) {
  const wktNorm = normalizeWktInput(wkt);
  if (!wktNorm || !Array.isArray(projectedPoints) || projectedPoints.length === 0) return null;

  const opts = proj4String ? { proj4String } : {};
  const converter = getWktToProj4Converter(wktNorm, opts);
  if (converter) {
    try {
      const sync = projectedPoints.map(([x, y]) => {
        const xNum = Number(x);
        const yNum = Number(y);
        if (!Number.isFinite(xNum) || !Number.isFinite(yNum)) return null;
        const [lng, lat] = converter.inverse([xNum, yNum]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng };
      });
      if (!sync.some((p) => !p)) return sync;
    } catch (e) {
      // fall through to server-side transform
    }
  }

  try {
    const pts = await wktTransformPoints(wktNorm, projectedPoints, 'to_wgs84');
    if (!Array.isArray(pts) || pts.length !== projectedPoints.length) return null;
    const out = pts.map((p) => {
      const lng = Number(p?.[0]);
      const lat = Number(p?.[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { lat, lng };
    });
    if (out.some((p) => !p)) return null;
    return out;
  } catch (e) {
    console.warn('projectedPointsToLatLngAsync failed', e);
    return null;
  }
}

/**
 * @param {{ min_x: number, max_x: number, min_y: number, max_y: number }} bbox
 * @param {string} wkt
 * @param {string|null|undefined} proj4String from /api/wkt/validate crs.proj4
 * @returns {Promise<Array<{ lat: number, lng: number }> | null>} corners in order: TL, TR, BR, BL
 */
export async function projectedBboxToLatLngQuadAsync(bbox, wkt, proj4String) {
  const wktNorm = normalizeWktInput(wkt);
  const opts = proj4String ? { proj4String } : {};
  const syncBounds = projectedBboxToLatLngBounds(bbox, wktNorm, opts);
  if (
    !bbox ||
    typeof bbox.min_x !== 'number' ||
    typeof bbox.max_x !== 'number' ||
    typeof bbox.min_y !== 'number' ||
    typeof bbox.max_y !== 'number'
  ) {
    return null;
  }

  const corners = [
    [bbox.min_x, bbox.max_y], // top-left
    [bbox.max_x, bbox.max_y], // top-right
    [bbox.max_x, bbox.min_y], // bottom-right
    [bbox.min_x, bbox.min_y], // bottom-left
  ];
  const quad = await projectedPointsToLatLngAsync(corners, wktNorm, proj4String);
  if (quad) return quad;

  // Keep compatibility with existing bbox-only flow if quad conversion fails.
  if (syncBounds) {
    return [
      { lat: syncBounds.north, lng: syncBounds.west },
      { lat: syncBounds.north, lng: syncBounds.east },
      { lat: syncBounds.south, lng: syncBounds.east },
      { lat: syncBounds.south, lng: syncBounds.west },
    ];
  }
  return null;
}

/**
 * @param {Array<{ lat: number, lng: number }>} points
 * @param {string} wkt
 * @param {number} z
 * @param {string|null|undefined} proj4String
 */
export async function latLngToProjectedXYAsync(points, wkt, z = 0, proj4String) {
  if (!Array.isArray(points) || points.length === 0) return [];
  const wktNorm = normalizeWktInput(wkt);
  const opts = proj4String ? { proj4String } : {};
  const syncTry = latLngToProjectedXY(points, wktNorm, z, opts);
  const syncOk =
    syncTry.length === points.length &&
    syncTry.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (syncOk) return syncTry;

  const payload = [];
  const indexMap = [];
  points.forEach((p, idx) => {
    const lat = Number(p.lat);
    const lng = Number(p.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      indexMap.push(idx);
      payload.push([lng, lat]);
    }
  });
  if (!payload.length) {
    return points.map(() => [NaN, NaN, z]);
  }

  try {
    const xy = await wktTransformPoints(wktNorm, payload, 'from_wgs84');
    if (!xy || xy.length !== payload.length) {
      return syncTry.length === points.length ? syncTry : [];
    }
    const out = Array.from({ length: points.length }, () => [NaN, NaN, z]);
    xy.forEach(([x, y], j) => {
      const idx = indexMap[j];
      out[idx] = [x, y, z];
    });
    return out;
  } catch (e) {
    console.warn('latLngToProjectedXYAsync failed', e);
    return syncTry.length === points.length ? syncTry : [];
  }
}
